<template>
  <long-query-layout
    resource-name="gourami"
    :query-filter="queryFilter"
    :map-query-filter-for-api="mapQueryFilterForApi"
    :apply-query-filter="applyQueryFilter"
    :result-fetch-url.sync="resultTable.url"
    :columns.sync="resultTable.columns"
    :group-headers.sync="resultTable.groupHeaders"
    :active-tab.sync="activeTab"
    :customer-download-only="true"
    :hide-restricted-mode="true"
    :excel-properties-handler="excelPropertiesHandler.bind(this)"
    @query-completed="toggleDetail(isDetail)"
    @result-area-size-change="updateTableSize"
  >
    <!-- -------------------------------------------------------- -->
    <!-- ヘッダのアクション -->
    <!-- -------------------------------------------------------- -->
    <template v-slot:header-actions="slotProps">
      <gl-switch
        :value="isDetail"
        :disabled="!(slotProps.queryId && activeTab == 'result')"
        @input="toggleDetail"
        >詳細</gl-switch
      >
    </template>
    <!-- -------------------------------------------------------- -->
    <!-- 分析条件 -->
    <!-- -------------------------------------------------------- -->
    <template v-slot:filter>
      <!-- 期間 -->
      <query-filter-element id="term-single" title="期間" contentWidth="medium">
        <filter-term-single v-bind.sync="queryFilter.term" />
      </query-filter-element>

      <!-- 商品 -->
      <query-filter-element id="item-single" title="商品">
        <gl-select-company :company.sync="queryFilter.item.company" />
        <!-- <filter-item v-bind.sync="queryFilter.item" /> -->
        <filter-item-single v-bind.sync="queryFilter.item" />
      </query-filter-element>

      <!-- オプション -->
      <query-filter-element id="option-gourami" title="オプション">
        <filter-option-gourami v-bind.sync="queryFilter.option" />
      </query-filter-element>
    </template>

    <!-- -------------------------------------------------------- -->
    <!-- 分析結果 -->
    <!-- -------------------------------------------------------- -->
    <template v-slot:result>
      <gl-table ref="myTable" v-bind="resultTable" />
    </template>
  </long-query-layout>
</template>

<script>
// 分析条件, 分析結果のためのレイアウト
import LongQueryLayout from '@/components/layout/LongQueryLayout.vue'
// 分析条件
import QueryFilterElement from '@/components/QueryFilterElement.vue'
import FilterTermSingle from '@/components/FilterTermSingle.vue'
import FilterItemSingle from '@/components/FilterItemSingle.vue'
import FilterOptionGourami from '@/components/FilterOptionGourami.vue'
// 分析条件モデル
import termSingle from '@/queryfilter/termSingle'
// import item from '@/queryfilter/item'
import item from '@/queryfilter/itemSingle'
// 分析結果
import GlTable from '@/components/GlTable.vue'

import { searchCellCustomFunction } from '@/util/custom_formatters'

export default {
  components: {
    QueryFilterElement,
    FilterTermSingle,
    FilterItemSingle,
    FilterOptionGourami,
    LongQueryLayout,
    GlTable,
  },
  data() {
    return {
      /**
       * 表示タブ（分析条件、分析結果）
       *
       * 表示タブをプログラム側から切り替えたい場合をこれを使います。
       */
      activeTab: undefined,
      /**
       * 分析条件
       */
      queryFilter: {
        term: {
          order: 1,
          summaryKey: 'termSingle',
          ...termSingle.get(),
          termType: 'm',
          visibleTermTypes: ['m'],
        },
        item: {
          order: 2,
          summaryKey: 'itemSingle',
          ...item.get(),
        },
        option: {
          order: 3,
          summaryKey: 'optionGourami',
          compare: 'item',
          sort: 'compare',
          expect: false,
          errors: [],
        },
      },
      /**
       * 分析結果
       */
      resultTable: {
        columns: undefined,
        groupHeaders: undefined,
        sortColumnName: 'base_compatible_rank',
        sortOrder: 'asc',
        url: undefined,
        width: 0,
        height: 0,
        customFormatter: {
          headerEdit: (cell, headerText, headerContent) => {
            if (!headerContent.border.top) {
              cell.style.borderTopStyle = 'none'
            }
            if (!headerContent.border.right) {
              cell.style.borderRightStyle = 'none'
            }
            if (!headerContent.border.bottom) {
              cell.style.borderBottomStyle = 'none'
            }
            if (!headerContent.border.left) {
              cell.style.borderLeftStyle = 'none'
            }
            cell.style.textAlign = headerContent.align
          },
          linkify: () => {},
          linkifySearch: searchCellCustomFunction,
        },
      },
      /**
       * この画面が持つ分析条件を、クエリのペイロードに変換する
       */
      mapQueryFilterForApi: queryFilter => ({
        term: termSingle.mapToApiPayload(queryFilter.term),
        item: item.mapToApiPayload(queryFilter.item),
        option: {
          compare: queryFilter.option.compare,
          sort: queryFilter.option.sort,
          expect: queryFilter.option.expect,
        },
      }),
      /**
       * 渡された分析条件をこの画面の分析条件に反映する
       *
       * 渡される分析条件はこの画面のものとは限らないので、"可能な限り"復元する。
       */
      applyQueryFilter: queryFilter => {
        termSingle.applyPayload(this.queryFilter.term, queryFilter.term)
        item.applyPayload(this.queryFilter.item, queryFilter.item)
        this.queryFilter.option.compare = queryFilter.option.compare
        this.queryFilter.option.sort = queryFilter.option.sort
        this.queryFilter.option.expect = queryFilter.option.expect
      },
      /**
       * 詳細表示
       */
      isDetail: false,
      /**
       * Excel出力のパラメータ生成
       */
      excelPropertiesHandler() {
        const { name, order } = this.$refs.myTable.getCurrentSort()
        return {
          sortColumnName: name,
          sortOrder: order,
          showDetail: this.isDetail,
        }
      },
    }
  },
  methods: {
    /**
     * 結果表示テーブルのサイズを更新する
     */
    updateTableSize(size) {
      this.resultTable.width = size.width
      this.resultTable.height = size.height
    },
    toggleDetail(value) {
      this.isDetail = value

      if (this.queryFilter.option) {
        if (this.queryFilter.option.sort === 'compare') {
          this.resultTable.sortColumnName = 'compare_compatible_rank'
        } else {
          this.resultTable.sortColumnName = 'base_compatible_rank'
        }
      }
      const alwaysShowColumns = [
        'compare_code',
        'compare_name',
        'concurrent_lift_icon',
        'concurrent_lift',
        'together_lift_icon',
        'together_lift',
        'expect_volume_icon',
        'expect_volume',
        'compare_compatible_rank',
        'compare_concurrent_confidence',
        'compare_together_confidence',
        'compare_expect_customer_transfer',
      ]

      this.resultTable.columns.forEach(c => {
        if (!alwaysShowColumns.includes(c.colName)) {
          c.hidden = !value
        }
      })

      //TODO column変えているが、これを入れないと再読み込みされない
      //だが、これだと3回もdataがfetchされているみたい
      // -> 多分大丈夫そう（環境依存の問題だったら要再調査
      this.$refs.myTable.init()
    },
  },
}
</script>
